import { useEffect, useRef } from 'preact/hooks';
import { CLASS_LIST, STATUS_LABEL, canStartRun, classPortraitUrl, classStatus, moveInGrid, type ClassEntry } from '../classes';
import { Padlock } from './Padlock';
import { useEscape } from './useEscape';

function cardLabel(c: ClassEntry): string {
  const st = classStatus(c.id);
  if (st === 'playable') return `Jouer (${c.name === 'Xélor' ? 'Xelor' : c.name})`;
  return `${c.name}, ${c.role}, ${STATUS_LABEL[st]}`;
}

interface Props {
  /** Classe mise en avant à l'ouverture (la classe mémorisée). */
  selected: string;
  onPlay: (classId: string) => void;
  onBack: () => void;
}

/** Menu « Choix de la classe » : 18 portraits, Xélor, Sacrieur et Sadida sont jouables (flèches, Entrée, Échap, toucher). */
export function ClassSelect({ selected, onPlay, onBack }: Props) {
  const grid = useRef<HTMLUListElement>(null);
  const buttons = () => Array.from(grid.current?.querySelectorAll<HTMLButtonElement>('button.class-card') ?? []);

  useEffect(() => {
    const idx = Math.max(0, CLASS_LIST.findIndex((c) => c.id === selected));
    buttons()[idx]?.focus({ preventScroll: false });
  }, []);

  // Échap quitte même quand le focus est tombé sur le fond (écouteur partagé avec les autres panneaux du hub)
  useEscape(onBack);

  const onKeyDown = (e: KeyboardEvent) => {
    const list = buttons();
    const from = list.findIndex((b) => b === document.activeElement);
    if (from < 0) return;
    const cols = (grid.current ? getComputedStyle(grid.current).gridTemplateColumns.split(' ').length : 1) || 1;
    const to = moveInGrid(from, e.key, cols, list.length);
    if (to !== from) { e.preventDefault(); list[to]?.focus(); }
  };

  return (
    <div class="panel class-select" role="dialog" aria-label="Choix de la classe" onKeyDown={onKeyDown}>
      <div class="class-head">
        <button class="class-back" type="button" onClick={onBack}>Retour</button>
        <div class="class-title">
          <h2>Choix de la classe</h2>
          <p>Xélor, Sacrieur et Sadida sont jouables.</p>
        </div>
      </div>
      <div class="class-scroll">
        <ul class="class-grid" ref={grid}>
          {CLASS_LIST.map((c) => {
            const st = classStatus(c.id);
            const playable = canStartRun(c.id);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  class={`class-card is-${st} art-${c.art}${c.id === selected ? ' is-selected' : ''}`}
                  data-class={c.id}
                  data-status={st}
                  aria-label={cardLabel(c)}
                  aria-disabled={playable ? undefined : 'true'}
                  onClick={() => { if (playable) onPlay(c.id); }}
                >
                  <span class="class-art">
                    <img src={classPortraitUrl(c.id)} alt="" draggable={false} loading="lazy" />
                    {st === 'locked' && <Padlock />}
                  </span>
                  <span class="class-name">{c.name}</span>
                  <span class="class-role">{c.role}</span>
                  <span class="class-status">{playable ? 'Jouer' : STATUS_LABEL[st]}</span>
                  {playable && <span class="class-flag">Jouable</span>}
                </button>
              </li>
            );
          })}
        </ul>
        <p class="credits">DOFUS, son univers, ses personnages et ses graphismes sont la propriété d'Ankama. Projet de fan indépendant, non officiel et non commercial.</p>
      </div>
    </div>
  );
}
